/**
 * CategoryExposureAlertsForm - Exposure alert configuration with two modes:
 *
 * Standard Tab: Common exposures (web-login, llm-chatbot)
 * - Managed via `exposure_settings` setting
 * - Two toggles per type: Enabled (save to DB) and Notify (send alerts)
 *
 * Advanced Tab: Port/protocol-based detection
 * - Managed via condition alerts
 * - Category-based selection + custom ports/protocols
 *
 * Pattern: Follows RateLimitingForm pattern with local state + batch save.
 */

import { Tabs } from '@praetorian-chariot/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { Loader } from '@/components/Loader';
import {
  SearchableTagInput,
  Suggestion,
} from '@/components/SearchableTagInput';
import { ToggleSwitch } from '@/components/ui';
import { useAddAlert, useRemoveAlert } from '@/hooks/useAlerts';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import { CategoryCard } from '@/sections/settings/components/cards/CategoryCard';
import {
  deriveSelectedCategories,
  EXPOSURE_CATEGORIES,
  getAllCategoryPorts,
  getCategoriesBySeverity,
  getCustomPorts,
  getCustomProtocols,
} from '@/sections/settings/components/cards/exposureAlertCategories';
import {
  PORT_SUGGESTIONS,
  PROTOCOL_SUGGESTIONS,
} from '@/sections/settings/components/cards/exposureAlertConstants';
import { useAuth } from '@/state/auth';
import { Condition, Setting } from '@/types';
import { getAlertFromAttributeKey } from '@/utils/alert.util';

/**
 * Tab values for Standard/Advanced selection modes.
 */
type TabValue = 'standard' | 'advanced';

/**
 * Tab configuration for Standard/Advanced selection modes.
 */
const EXPOSURE_TABS: { label: string; value: TabValue }[] = [
  { label: 'Standard', value: 'standard' },
  { label: 'Advanced', value: 'advanced' },
];

/**
 * Exposure settings type - maps risk names to notification preferences
 */
type ExposureSettings = Record<string, boolean>;

/**
 * Configuration for common exposure types (Standard tab)
 */
interface CommonExposureConfig {
  id: string;
  name: string;
  description: string;
}

/**
 * Common exposure types for Standard tab
 */
const COMMON_EXPOSURES: CommonExposureConfig[] = [
  {
    id: 'web-login',
    name: 'Web Logins',
    description: 'Login forms, admin panels, authentication pages',
  },
  {
    id: 'llm-chatbot',
    name: 'LLM Chatbots',
    description: 'Exposed AI/LLM endpoints (Ollama, OpenAI-compatible, etc.)',
  },
];

/**
 * State for each common exposure type
 */
interface ExposureState {
  enabled: boolean;
  notify: boolean;
}

interface CategoryExposureAlertsFormProps {
  /**
   * Email for impersonation - if provided, will fetch data for this user
   */
  customerEmail?: string;
  /**
   * Called when modal should close (after successful save or cancel)
   */
  onClose?: () => void;
}

/**
 * Helper to compare sets for equality
 */
function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

/**
 * CategoryExposureAlertsForm provides exposure alert configuration.
 */
export function CategoryExposureAlertsForm({
  customerEmail,
  onClose,
}: CategoryExposureAlertsFormProps) {
  const { me, friend } = useAuth();
  // Use same email logic as useUpdateSetting to ensure query key matches for invalidation
  const effectiveEmail = customerEmail || friend || me;

  // Memoize query options to provide stable references
  const conditionQueryResource = useMemo(
    () => ({ resource: 'condition' as const }),
    []
  );
  const settingQueryResource = useMemo(
    () => ({ resource: 'setting' as const }),
    []
  );
  const queryOptions = useMemo(
    () => ({ email: effectiveEmail }),
    [effectiveEmail]
  );

  // Fetch current conditions (for Advanced tab)
  const {
    data: conditions,
    status: conditionsStatus,
    refetch: refetchConditions,
  } = useMy(conditionQueryResource, queryOptions);

  // Fetch current settings (for Standard tab)
  const {
    data: settings,
    status: settingsStatus,
    refetch: refetchSettings,
  } = useMy(settingQueryResource, queryOptions);

  const { mutateAsync: addAlert } = useAddAlert();
  const { mutateAsync: removeAlert } = useRemoveAlert();
  const { mutateAsync: updateSetting } = useUpdateSetting(customerEmail);

  // ========== Standard Tab State (Common Exposures) ==========
  const [exposureStates, setExposureStates] = useState<
    Record<string, ExposureState>
  >({});
  const [initialExposureStates, setInitialExposureStates] = useState<
    Record<string, ExposureState>
  >({});

  // ========== Advanced Tab State (Port/Protocol Categories) ==========
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set()
  );
  const [customPorts, setCustomPorts] = useState<string[]>([]);
  const [customProtocols, setCustomProtocols] = useState<string[]>([]);
  const [initialCategories, setInitialCategories] = useState<Set<string>>(
    new Set()
  );
  const [initialCustomPorts, setInitialCustomPorts] = useState<string[]>([]);
  const [initialCustomProtocols, setInitialCustomProtocols] = useState<
    string[]
  >([]);

  // ========== Shared State ==========
  const [activeTab, setActiveTab] = useState<TabValue>('standard');
  const [isSaving, setIsSaving] = useState(false);

  // Initialization guards
  const conditionsInitializedRef = useRef(false);
  const settingsInitializedRef = useRef(false);

  // Initialize Standard tab state from settings
  useEffect(() => {
    if (
      settingsStatus === 'success' &&
      settings &&
      !settingsInitializedRef.current
    ) {
      const exposureSettingRaw = settings.find(
        (s: Setting) => s.name === 'exposure_settings'
      );
      const exposureSettings: ExposureSettings =
        (exposureSettingRaw?.value as ExposureSettings) || {};

      const states: Record<string, ExposureState> = {};
      COMMON_EXPOSURES.forEach(type => {
        const isConfigured = type.id in exposureSettings;
        states[type.id] = {
          enabled: isConfigured,
          notify: isConfigured ? exposureSettings[type.id] : false,
        };
      });

      setExposureStates(states);
      // Deep clone for change detection
      const initialStatesCopy: Record<string, ExposureState> = {};
      Object.entries(states).forEach(([key, value]) => {
        initialStatesCopy[key] = { ...value };
      });
      setInitialExposureStates(initialStatesCopy);
      settingsInitializedRef.current = true;
    }
  }, [settings, settingsStatus]);

  // Initialize Advanced tab state from conditions
  useEffect(() => {
    if (
      conditionsStatus === 'success' &&
      conditions &&
      !conditionsInitializedRef.current
    ) {
      const activePorts = new Set<number>();
      const activeProtocols = new Set<string>();

      ((conditions || []) as Condition[]).forEach(condition => {
        if (condition.name === 'port') {
          activePorts.add(Number(condition.value));
        } else if (condition.name === 'protocol') {
          activeProtocols.add(condition.value);
        }
      });

      const selected = deriveSelectedCategories(activePorts);
      const customs = getCustomPorts(activePorts);
      const customProtos = getCustomProtocols(activeProtocols);

      setSelectedCategories(new Set(selected));
      setCustomPorts(customs.map(String));
      setCustomProtocols(customProtos);

      setInitialCategories(new Set(selected));
      setInitialCustomPorts(customs.map(String));
      setInitialCustomProtocols(customProtos);

      conditionsInitializedRef.current = true;
    }
  }, [conditionsStatus, conditions]);

  // Check if Standard tab has changes
  const hasStandardChanges = useMemo(() => {
    return COMMON_EXPOSURES.some(type => {
      const current = exposureStates[type.id];
      const initial = initialExposureStates[type.id];
      if (!current || !initial) return false;
      return (
        current.enabled !== initial.enabled || current.notify !== initial.notify
      );
    });
  }, [exposureStates, initialExposureStates]);

  // Check if Advanced tab has changes
  const hasAdvancedChanges = useMemo(() => {
    if (!setsEqual(selectedCategories, initialCategories)) return true;
    if (!setsEqual(new Set(customPorts), new Set(initialCustomPorts)))
      return true;
    if (!setsEqual(new Set(customProtocols), new Set(initialCustomProtocols)))
      return true;
    return false;
  }, [
    selectedCategories,
    initialCategories,
    customPorts,
    initialCustomPorts,
    customProtocols,
    initialCustomProtocols,
  ]);

  const hasChanges = hasStandardChanges || hasAdvancedChanges;

  // ========== Standard Tab Handlers ==========
  const handleEnabledToggle = (id: string, enabled: boolean) => {
    setExposureStates(prev => ({
      ...prev,
      [id]: {
        enabled,
        notify: enabled ? prev[id]?.notify || false : false,
      },
    }));
  };

  const handleNotifyToggle = (id: string, notify: boolean) => {
    setExposureStates(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        notify,
      },
    }));
  };

  // ========== Advanced Tab Handlers ==========
  const handleToggleCategory = (categoryId: string) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleAddCustomPort = async (value: string) => {
    if (!customPorts.includes(value)) {
      setCustomPorts(prev => [...prev, value]);
    }
  };

  const handleRemoveCustomPort = async (value: string) => {
    setCustomPorts(prev => prev.filter(p => p !== value));
  };

  const handleAddCustomProtocol = async (value: string) => {
    if (!customProtocols.includes(value)) {
      setCustomProtocols(prev => [...prev, value]);
    }
  };

  const handleRemoveCustomProtocol = async (value: string) => {
    setCustomProtocols(prev => prev.filter(p => p !== value));
  };

  const customPortSuggestions = useMemo((): Suggestion[] => {
    const categoryPorts = getAllCategoryPorts();
    return PORT_SUGGESTIONS.filter(s => !categoryPorts.has(Number(s.value)));
  }, []);

  // ========== Save Handler ==========
  const handleApply = async () => {
    setIsSaving(true);

    try {
      // Save Standard tab (exposure_settings)
      if (hasStandardChanges) {
        const exposureSettings: ExposureSettings = {};
        COMMON_EXPOSURES.forEach(type => {
          const state = exposureStates[type.id];
          if (state?.enabled) {
            exposureSettings[type.id] = state.notify;
          }
        });

        await updateSetting({
          name: 'exposure_settings',
          value: exposureSettings,
          silent: true,
        });
      }

      // Save Advanced tab (conditions)
      if (hasAdvancedChanges) {
        // Calculate target ports from categories + custom
        const targetPorts = new Set<number>();
        selectedCategories.forEach(categoryId => {
          const category = EXPOSURE_CATEGORIES.find(c => c.id === categoryId);
          if (category) {
            category.ports.forEach(port => targetPorts.add(port));
          }
        });
        customPorts.forEach(port => targetPorts.add(Number(port)));

        // Get current ports
        const currentPorts = new Set<number>();
        ((conditions || []) as Condition[]).forEach(condition => {
          if (condition.name === 'port') {
            currentPorts.add(Number(condition.value));
          }
        });

        // Calculate diff
        const portsToAdd = Array.from(targetPorts).filter(
          p => !currentPorts.has(p)
        );
        const portsToRemove = Array.from(currentPorts).filter(
          p => !targetPorts.has(p)
        );

        // Protocol diff
        const currentProtocols = new Set<string>();
        ((conditions || []) as Condition[]).forEach(condition => {
          if (condition.name === 'protocol') {
            currentProtocols.add(condition.value);
          }
        });

        const targetProtocols = new Set(customProtocols);
        const protocolsToAdd = customProtocols.filter(
          p => !currentProtocols.has(p)
        );
        const protocolsToRemove = Array.from(currentProtocols).filter(
          p => !targetProtocols.has(p)
        );

        // Apply port changes
        for (const port of portsToAdd) {
          const alert = getAlertFromAttributeKey(`#attribute#port#${port}`);
          if (alert) {
            await addAlert({
              value: alert.value,
              name: alert.name,
              silent: true,
            });
          }
        }
        for (const port of portsToRemove) {
          const alert = getAlertFromAttributeKey(`#attribute#port#${port}`);
          if (alert) {
            await removeAlert({ key: alert.key, silent: true });
          }
        }

        // Apply protocol changes
        for (const protocol of protocolsToAdd) {
          const alert = getAlertFromAttributeKey(
            `#attribute#protocol#${protocol}`
          );
          if (alert) {
            await addAlert({
              value: alert.value,
              name: alert.name,
              silent: true,
            });
          }
        }
        for (const protocol of protocolsToRemove) {
          const alert = getAlertFromAttributeKey(
            `#attribute#protocol#${protocol}`
          );
          if (alert) {
            await removeAlert({ key: alert.key, silent: true });
          }
        }

        await refetchConditions();
      }

      // Refetch settings to update the card display
      await refetchSettings();

      toast.success('Exposure alerts updated');
      onClose?.();
    } catch (error) {
      console.error('Failed to update exposure alerts:', error);
      toast.error('Failed to update exposure alerts');
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading =
    conditionsStatus === 'pending' || settingsStatus === 'pending';

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="category-exposure-alerts-form"
    >
      <Loader
        isLoading={isLoading}
        className="mx-auto flex justify-center py-8"
      >
        {/* Description */}
        <p className="text-chariot-text-secondary text-sm">
          Configure exposure detection and notification preferences.
        </p>

        {/* Standard/Advanced Tabs */}
        <Tabs
          tabs={EXPOSURE_TABS}
          selected={
            EXPOSURE_TABS.find(t => t.value === activeTab) || EXPOSURE_TABS[0]
          }
          onSelect={tab => setActiveTab(tab.value as TabValue)}
        />

        {/* Standard Tab - Common Exposures */}
        {activeTab === 'standard' && (
          <div className="divide-chariot-stroke border-chariot-stroke divide-y rounded-lg border">
            {COMMON_EXPOSURES.map(type => {
              const state = exposureStates[type.id] || {
                enabled: false,
                notify: false,
              };
              return (
                <div
                  key={type.id}
                  className="flex items-center justify-between p-4"
                  data-testid={`exposure-row-${type.id}`}
                >
                  {/* Left side - Name and Description */}
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{type.name}</span>
                    <span className="text-chariot-text-secondary text-xs">
                      {type.description}
                    </span>
                  </div>

                  {/* Right side - Toggles */}
                  <div className="flex items-center gap-6">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-chariot-text-secondary text-xs">
                        Enabled
                      </span>
                      <ToggleSwitch
                        id={`${type.id}-enabled`}
                        checked={state.enabled}
                        onChange={checked =>
                          handleEnabledToggle(type.id, checked)
                        }
                        disabled={isSaving}
                      />
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-chariot-text-secondary text-xs">
                        Notify
                      </span>
                      <ToggleSwitch
                        id={`${type.id}-notify`}
                        checked={state.notify}
                        onChange={checked =>
                          handleNotifyToggle(type.id, checked)
                        }
                        disabled={isSaving || !state.enabled}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Advanced Tab - Port/Protocol Categories + Custom */}
        {activeTab === 'advanced' && (
          <div className="flex flex-col gap-6">
            {/* Category Grid */}
            <div className="divide-chariot-stroke border-chariot-stroke grid grid-cols-[5rem_1fr_3rem] divide-y overflow-hidden rounded-lg border">
              {getCategoriesBySeverity().map(category => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  selected={selectedCategories.has(category.id)}
                  onToggle={() => handleToggleCategory(category.id)}
                  disabled={isSaving}
                />
              ))}
            </div>

            {/* Custom Ports/Protocols */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <h5 className="text-sm font-semibold">Custom Ports</h5>
                <SearchableTagInput
                  selectedValues={customPorts}
                  suggestions={customPortSuggestions}
                  placeholder="Search or add port..."
                  onAdd={handleAddCustomPort}
                  onRemove={handleRemoveCustomPort}
                  disabled={isSaving}
                  testId="advanced-ports"
                />
              </div>
              <div className="flex flex-col gap-2">
                <h5 className="text-sm font-semibold">Custom Protocols</h5>
                <SearchableTagInput
                  selectedValues={customProtocols}
                  suggestions={PROTOCOL_SUGGESTIONS}
                  placeholder="Search or add protocol..."
                  onAdd={handleAddCustomProtocol}
                  onRemove={handleRemoveCustomProtocol}
                  disabled={isSaving}
                  testId="advanced-protocols"
                />
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2 pt-4">
          {onClose && (
            <Button
              label="Cancel"
              styleType="secondary"
              onClick={onClose}
              disabled={isSaving}
              data-testid="category-alerts-cancel-button"
            />
          )}
          <Button
            label="Apply"
            styleType="primary"
            onClick={handleApply}
            disabled={!hasChanges || isSaving}
            data-testid="category-alerts-apply-button"
          />
        </div>
      </Loader>
    </div>
  );
}
